/**
 * Settings → System → AI provider — ADR-004 §4, §6, §7 (operator decisions 6, 7, 9, 10 of the follow-up interview:
 * the provider is a system-admin setting, the Claude CLI at go-live, `system.admin` = the founder only).
 *
 * `system.admin` is the `settings.system` capability: held by the founder role, NOT grantable (decision 10), so the
 * capability grant routes (PUT/DELETE /users/{id}/capabilities/{capability}) do not offer it. It adds no data sight.
 *
 * Provider keys are write-only (§6.1): stored AES-256-GCM encrypted under LLM_SECRET_KEK in `provider_secrets`,
 * never returned — only `last4`, who set it and when. They are decrypted in the worker only, which is also where a
 * test call runs: POST /settings/llm/test answers 202 and the result lands on GET /settings/llm.
 */
import { z } from 'zod';
import { IsoDate, Timestamp, Version } from './common.js';
import { UserRef } from './users.js';
/** The selectable providers. (`stub` exists only in staging, via `stub_in_staging`, and is never selectable.) */
export const LlmProviderId = z.enum(['anthropic', 'openai', 'claude_cli']).meta({ id: 'LlmProviderId' });
/** Providers that need an API key. The Claude CLI uses the worker's subscription login instead (decision 9). */
export const KeyedLlmProvider = z.enum(['anthropic', 'openai']).meta({ id: 'KeyedLlmProvider' });
export const LLM_PROVIDER_LABEL = {
    anthropic: 'Anthropic API',
    openai: 'OpenAI API',
    claude_cli: 'Claude CLI (subscription)',
};
/** The four uses of a language model (ADR-004 §4). */
export const LlmPurpose = z.enum(['capture_propose', 'email_tone', 'email_propose', 'meeting_summary']).meta({ id: 'LlmPurpose' });
export const LLM_PURPOSES = LlmPurpose.options;
export const LLM_PURPOSE_LABEL = {
    capture_propose: 'Voice and text notes → tasks',
    email_tone: 'Email tone',
    email_propose: 'Email → proposed tasks',
    meeting_summary: 'Meeting summaries',
};
/** A model id: free text checked against `known_models` (so a new model is a Settings edit, not a deploy). */
const ModelId = z.string().trim().min(1).max(100).regex(/^[A-Za-z0-9._:\/-]+$/, 'Letters, digits and . _ : / - only');
export const LlmPurposeSetting = z
    .object({
    provider: LlmProviderId,
    /** `default` lets the Claude CLI choose (only valid for `claude_cli`). */
    model: ModelId,
    max_output_tokens: z.number().int().min(64).max(32_000),
    timeout_ms: z.number().int().min(5_000).max(600_000),
})
    .meta({ id: 'LlmPurposeSetting' });
/** USD per 1M tokens, by model id. Historical llm_calls keep the cost computed at the time (§6.3). */
export const LlmPrice = z.object({ in: z.number().nonnegative().max(1000), out: z.number().nonnegative().max(1000) }).strict().meta({ id: 'LlmPrice' });
/**
 * The latest test call for a provider. `running` until the worker answers; a test the worker has not picked up
 * within 5 minutes reads as `failed` ("the worker did not run the test"), never as running forever.
 */
export const LlmCheck = z
    .object({
    status: z.enum(['running', 'ok', 'failed']),
    /** The model the test asked for. */
    model: ModelId,
    requested_at: Timestamp,
    requested_by: UserRef.nullable(),
    finished_at: Timestamp.nullable(),
    latency_ms: z.number().int().nonnegative().nullable(),
    /** The model that actually answered, as the provider reported it. */
    model_answered: z.string().nullable(),
    /** Whether the answer validated against our schema (§2.1). */
    json_valid: z.boolean().nullable(),
    /** Plain-English reason on failure, e.g. "HTTP 401: invalid x-api-key". Never contains key material. */
    error: z.string().nullable(),
})
    .meta({ id: 'LlmCheck' });
export const LlmProviderStatus = z
    .object({
    provider: LlmProviderId,
    label: z.string(),
    needs_key: z.boolean(),
    /** The stored key, write-only: only its last four characters are ever shown. `null` = no key. */
    key: z
        .object({ last4: z.string().length(4), set_by: UserRef.nullable(), set_at: Timestamp })
        .nullable(),
    /** A keyed provider has a key; the CLI is always configured. */
    configured: z.boolean(),
    last_check: LlmCheck.nullable(),
    /**
     * May be selected for a purpose: configured AND the latest test is `ok` AND (keyed) that test ran after the
     * key was last set. Changing the key makes it untested again.
     */
    ready: z.boolean(),
    /** When not ready: what is missing, e.g. "Add an API key" / "Run a test — the key changed after the last one". */
    not_ready_reason: z.string().nullable(),
    /** Purposes (and the default) that name this provider now. */
    in_use_by: z.array(z.union([LlmPurpose, z.literal('default')])),
})
    .meta({ id: 'LlmProviderStatus' });
/** GET /settings/llm — never any ciphertext or key. */
export const LlmSettingsResponse = z
    .object({
    providers: z.array(LlmProviderStatus),
    /** Used by any purpose without its own row. */
    default_provider: LlmProviderId,
    /** Every purpose, as it will run on its NEXT job (a change never affects a job in flight). */
    purposes: z.record(LlmPurpose, LlmPurposeSetting),
    /** Per-provider allow-list of model ids (§4). `claude_cli` also always accepts `default`. */
    known_models: z.record(LlmProviderId, z.array(ModelId)),
    prices: z.record(z.string(), LlmPrice),
    /** Metered providers stop at this many USD a day (IST); `null` = no ceiling. The CLI is not metered. */
    daily_spend_ceiling_usd: z.number().nonnegative().nullable(),
    /** Staging only (ADR-004 L5): every purpose runs on the deterministic stub. Ignored in production. */
    stub_in_staging: z.boolean(),
    /** Whether the server holds LLM_SECRET_KEK; without it keys cannot be stored (PUT is 422). */
    key_storage_ready: z.boolean(),
    config_version: Version,
    updated_at: Timestamp.nullable(),
    updated_by: UserRef.nullable(),
})
    .meta({ id: 'LlmSettingsResponse' });
/**
 * PATCH /settings/llm — only what changes. A purpose (or the default) may newly name only a `ready` provider, and
 * its model must be in that provider's `known_models` (after this patch) — 422 naming the field and what is missing.
 */
export const PatchLlmSettingsBody = z
    .object({
    default_provider: LlmProviderId.optional(),
    purposes: z.partialRecord(LlmPurpose, LlmPurposeSetting.partial().strict()).optional(),
    known_models: z.partialRecord(LlmProviderId, z.array(ModelId).max(50)).optional(),
    /** Replaces the price table. */
    prices: z.record(ModelId, LlmPrice).optional(),
    daily_spend_ceiling_usd: z.number().nonnegative().max(10_000).nullable().optional(),
    stub_in_staging: z.boolean().optional(),
})
    .strict()
    .refine((b) => Object.keys(b).length > 0, 'Send at least one setting to change');
export const LlmSecretParams = z.object({ provider: KeyedLlmProvider });
/** PUT /settings/llm/secrets/{provider} — write-only. The key is never echoed, logged or audited. */
export const PutLlmSecretBody = z
    .object({ api_key: z.string().trim().min(20, 'That is too short to be an API key').max(500).regex(/^\S+$/, 'An API key has no spaces') })
    .strict();
/** POST /settings/llm/test — one tiny real call per provider, run by the worker. */
export const TestLlmBody = z
    .object({
    /** Omit to test every configured provider. */
    provider: LlmProviderId.optional(),
    /** Omit to use the model the provider's purposes use (else its first known model; `default` for the CLI). */
    model: ModelId.optional(),
})
    .strict();
/** GET /settings/llm/usage — default this month (IST) to today. */
export const LlmUsageQuery = z
    .object({ from: IsoDate.optional(), to: IsoDate.optional() })
    .refine((q) => !q.from || !q.to || q.from <= q.to, { message: '`from` must be on or before `to`', path: ['from'] });
export const LlmUsageRow = z
    .object({
    provider: z.string(),
    model: z.string(),
    /** One of the four purposes, or `test` for Settings test calls. */
    purpose: z.string(),
    calls: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    repaired: z.number().int().nonnegative(),
    input_tokens: z.number().int().nonnegative(),
    output_tokens: z.number().int().nonnegative(),
    cached_input_tokens: z.number().int().nonnegative(),
    /** `null` when nothing in the group was metered (the CLI on the subscription, the stub). */
    cost_usd: z.number().nonnegative().nullable(),
    cost_basis: z.enum(['metered', 'subscription', 'none', 'mixed']),
    /** "Not metered (subscription)" for the CLI, "Stub — no model called" for the stub; else `null` (§6.3). */
    label: z.string().nullable(),
})
    .meta({ id: 'LlmUsageRow' });
export const LlmUsageResponse = z
    .object({
    from: IsoDate,
    to: IsoDate,
    /** Busiest first. */
    rows: z.array(LlmUsageRow),
    totals: z.object({
        calls: z.number().int().nonnegative(),
        metered_cost_usd: z.number().nonnegative(),
        /** Calls whose cost is not known per call (CLI subscription), reported as counts — never as $0. */
        not_metered_calls: z.number().int().nonnegative(),
    }),
    /** Today's (IST) metered spend against the ceiling. */
    today_metered_cost_usd: z.number().nonnegative(),
    daily_spend_ceiling_usd: z.number().nonnegative().nullable(),
})
    .meta({ id: 'LlmUsageResponse' });
// ---------------------------------------------------------------- Claude sign-in (operator request 17 Oct 2026)
/**
 * Settings → System → AI provider → Claude CLI: the founder signs in to Claude here instead of pasting
 * CLAUDE_CODE_OAUTH_TOKEN into the App Platform env. It is the same sign-in `claude setup-token` does — Claude's
 * OAuth with a pasted code, scope `user:inference`, a token good for a year — run by the server: start gives an
 * address to open; claude.ai then shows a code, which the founder pastes back to complete.
 *
 * The token is stored like an API key (provider_secrets, AES-256-GCM under LLM_SECRET_KEK, write-only: only last4
 * is ever shown) and the worker hands it to the CLI, ahead of any CLAUDE_CODE_OAUTH_TOKEN in the environment
 * (which stays as the fallback). Sign-in and sign-out are audited (provider, last4 — never the token).
 */
export const ClaudeLoginState = z
    .enum(['not_signed_in', 'pending', 'signed_in', 'expired', 'error'])
    .meta({ id: 'ClaudeLoginState' });
/** A sign-in that has been started and not yet completed. Open `auth_url`, sign in, paste the code Claude shows. */
export const ClaudeLoginStart = z
    .object({
    login_id: z.uuid(),
    /** The claude.ai address to open (Claude's OAuth authorize page). Carries no secret. */
    auth_url: z.url(),
    /** The code must be pasted before this (10 minutes from start); after it, start again. */
    expires_at: Timestamp,
})
    .meta({ id: 'ClaudeLoginStart' });
/** GET /settings/llm/claude-login. Never the token — only its last four characters. */
export const ClaudeLoginStatus = z
    .object({
    /**
     * `pending` — a sign-in is open (see `pending`), whether or not a token is stored already (a renewal).
     * `signed_in` — a token is stored and has not expired. `expired` — a token is stored but past `expires_at`.
     * `error` — no token, and the last sign-in ended in an error (`error` says what). `not_signed_in` — none.
     */
    state: ClaudeLoginState,
    /** The stored token (all null when none). `set_by` is who signed in, `set_at` when. */
    set_by: UserRef.nullable(),
    set_at: Timestamp.nullable(),
    /** When the stored token stops working (a year after sign-in): renewing is signing in again. */
    expires_at: Timestamp.nullable(),
    last4: z.string().length(4).nullable(),
    /** The open sign-in, so a page reload can carry on with it; null when none is open. */
    pending: ClaudeLoginStart.nullable(),
    /** Plain English: why the last attempt to complete failed (while `pending`), or why sign-in ended (`error`). */
    error: z.string().nullable(),
    /** Whether the server holds LLM_SECRET_KEK; without it the token cannot be stored (start is 422). */
    key_storage_ready: z.boolean(),
})
    .meta({ id: 'ClaudeLoginStatus' });
/** POST /settings/llm/claude-login/complete. `code` is what claude.ai shows after sign-in (it looks like `abc…#xyz…`). */
export const CompleteClaudeLoginBody = z
    .object({
    login_id: z.uuid(),
    code: z
        .string()
        .trim()
        .min(10, 'Paste the whole code claude.ai showed after you signed in')
        .max(2000)
        .regex(/^\S+$/, 'The code has no spaces — paste it exactly as claude.ai shows it'),
})
    .strict();
//# sourceMappingURL=llm.js.map